import { PmfSurvey, UsageStatisticsEntry } from '@griply/types';

import { diffInUtcDays, MS_PER_DAY, startOfUtcDay } from '../utc-day';

const MAX_SHOWN_COUNT = 3;
const COOLDOWN_DAYS = 7;
const MIN_ACCOUNT_AGE_DAYS = 21;
const ACTIVITY_WINDOW_DAYS = 21;
const RECENT_WINDOW_DAYS = 7;
const MIN_ACTIVE_DAYS = 5;
const MIN_VOLUME_WINDOW = 15;
const MIN_VOLUME_RECENT = 3;
const MAX_GAP_DAYS = 6;

export type IsPmfSurveyEligibleOptions = {
  pmfSurvey: PmfSurvey | undefined;
  usageStatistics: UsageStatisticsEntry[] | undefined;
  now: Date;
};

const isWithinWindow = (
  entry: UsageStatisticsEntry,
  now: Date,
  days: number,
) => {
  const age = diffInUtcDays(now, entry.date);
  return age >= 0 && age <= days;
};

export const isPmfSurveyEligible = ({
  pmfSurvey,
  usageStatistics,
  now,
}: IsPmfSurveyEligibleOptions): boolean => {
  if (pmfSurvey?.surveyCompleted) {
    return false;
  }

  if ((pmfSurvey?.surveyShownCount ?? 0) >= MAX_SHOWN_COUNT) {
    return false;
  }

  if (
    pmfSurvey?.lastTimeShown &&
    diffInUtcDays(now, pmfSurvey.lastTimeShown) < COOLDOWN_DAYS
  ) {
    return false;
  }

  const entries = usageStatistics ?? [];

  if (!entries.length) {
    return false;
  }

  const hasOldEnoughEntry = entries.some(
    (entry) => diffInUtcDays(now, entry.date) >= MIN_ACCOUNT_AGE_DAYS,
  );
  if (!hasOldEnoughEntry) {
    return false;
  }

  const windowEntries = entries.filter((entry) =>
    isWithinWindow(entry, now, ACTIVITY_WINDOW_DAYS),
  );

  const activeDays = windowEntries.filter(
    (entry) => entry.completionCount >= 1,
  ).length;
  if (activeDays < MIN_ACTIVE_DAYS) {
    return false;
  }

  const windowVolume = windowEntries.reduce(
    (sum, entry) => sum + entry.completionCount,
    0,
  );
  if (windowVolume < MIN_VOLUME_WINDOW) {
    return false;
  }

  const recentVolume = entries.reduce(
    (sum, entry) =>
      isWithinWindow(entry, now, RECENT_WINDOW_DAYS)
        ? sum + entry.completionCount
        : sum,
    0,
  );
  if (recentVolume < MIN_VOLUME_RECENT) {
    return false;
  }

  const uniqueDays = [
    ...new Set(
      windowEntries.map((entry) => startOfUtcDay(entry.date).getTime()),
    ),
  ].sort((a, b) => a - b);
  const hasLargeGap = uniqueDays.some(
    (day, index) =>
      index > 0 && (day - uniqueDays[index - 1]) / MS_PER_DAY > MAX_GAP_DAYS,
  );
  if (hasLargeGap) {
    return false;
  }

  return true;
};
